import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Mail, 
  Lock, 
  Loader2, 
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const { login, isAuthenticated } = useAuth();
  
  // State
  const [step, setStep] = useState('email'); // email, otp, success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  
  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = useRef([]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const destination = returnUrl ? decodeURIComponent(returnUrl) : '/portal';
      // Same domain - safe to auto-redirect
      window.location.href = `${window.location.origin}${destination}`;
    }
  }, [isAuthenticated, returnUrl]);

  // Password Login
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/auth/password-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password: password.trim(),
          remember_me: rememberMe
        })
      });
      
      // Read response as text first, then parse - avoids clone/body-read issues
      let data;
      try {
        const responseText = await response.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        throw new Error('Unable to process server response. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }
      
      // Check if password reset is required
      if (data.must_reset_password) {
        // Redirect to password reset
        window.location.href = `${window.location.origin}/portal/forgot-password?email=${encodeURIComponent(email)}`;
        return;
      }
      
      // Success - log in
      login(data.token, data.policies, data.email, data.policies[0], rememberMe);
      
      // Determine redirect destination
      let destination = returnUrl ? decodeURIComponent(returnUrl) : '/portal';
      
      // Show success then redirect (same domain, no cross-domain issues)
      setStep('success');
      const redirectUrl = `${window.location.origin}${destination}`;
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, 1500);
      
    } catch (err) {
      setError(getFriendlyErrorMessage(err.message));
    } finally {
      setLoading(false);
    }
  };

  // Convert technical error messages to user-friendly messages
  const getFriendlyErrorMessage = (errorMessage) => {
    const errorMap = {
      // Network/fetch errors
      'Body is disturbed or locked': 'Unable to connect. Please check your internet connection and try again.',
      'Failed to fetch': 'Unable to reach our servers. Please check your connection and try again.',
      'NetworkError': 'Network error. Please check your internet connection.',
      'Load failed': 'Connection failed. Please try again.',
      'Network request failed': 'Unable to connect. Please try again.',
      'AbortError': 'Request was cancelled. Please try again.',
      'TypeError: Failed to fetch': 'Connection error. Please check your internet and try again.',
      
      // Auth errors - keep these as-is but make them friendlier
      'Invalid email or password': 'The email or password you entered is incorrect. Please try again.',
      'Invalid credentials': 'The email or password you entered is incorrect. Please try again.',
      'User not found': 'No account found with this email address.',
      'Account is temporarily locked': 'Your account has been temporarily locked due to too many failed attempts. Please try again in 15 minutes.',
      'account_locked': 'Your account has been temporarily locked. Please try again in 15 minutes or reset your password.',
      
      // Server errors
      'Internal Server Error': 'Something went wrong on our end. Please try again in a moment.',
      '500': 'Our servers are experiencing issues. Please try again shortly.',
      '502': 'Service temporarily unavailable. Please try again in a moment.',
      '503': 'Service temporarily unavailable. Please try again in a moment.',
      '504': 'Request timed out. Please try again.',
      
      // JSON parsing errors
      'Unexpected token': 'Unable to process server response. Please try again.',
      'JSON': 'Unable to process server response. Please try again.',
      'SyntaxError': 'Unable to process server response. Please try again.',
      
      // Generic
      'Unable to process server response': 'Something went wrong. Please try again.',
    };

    // Check for exact match first
    if (errorMap[errorMessage]) {
      return errorMap[errorMessage];
    }

    // Check for partial matches
    for (const [key, friendlyMessage] of Object.entries(errorMap)) {
      if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
        return friendlyMessage;
      }
    }

    // If no match found, return a generic friendly message
    // but log the original for debugging
    console.error('Unhandled error:', errorMessage);
    return 'Something went wrong. Please try again or contact support if the issue persists.';
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      
      if (newOtp.every(d => d !== '')) {
        handleOtpSubmit(newOtp.join(''));
      }
    } else {
      const newOtp = [...otp];
      newOtp[index] = value.replace(/\D/g, '');
      setOtp(newOtp);
      
      if (value && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
      
      if (newOtp.every(d => d !== '')) {
        handleOtpSubmit(newOtp.join(''));
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Verify OTP
  const handleOtpSubmit = async (otpCode) => {
    const code = otpCode || otp.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: password.trim(),
          email: email.trim(),
          otp: code,
          remember_me: rememberMe
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      // Use AuthContext to store session
      login(data.token, data.policies, data.email, data.policy_number, rememberMe);
      
      const destination = returnUrl ? decodeURIComponent(returnUrl) : '/portal';
      
      // Show success then redirect (same domain, no cross-domain issues)
      setShowSuccessToast(true);
      setStep('success');
      setTimeout(() => {
        window.location.href = `${window.location.origin}${destination}`;
      }, 1500);
      
    } catch (err) {
      setError(getFriendlyErrorMessage(err.message));
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policy_number: password.trim(),
          email: email.trim()
        })
      });
      
      setOtp(['', '', '', '', '', '']);
      
    } catch (err) {
      setError(getFriendlyErrorMessage(err.message || 'Failed to resend code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Success Toast - Top Right */}
      {showSuccessToast && (
        <div className="fixed top-6 right-6 z-50 animate-slide-in">
          <div className="bg-[#10B981] text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Welcome back!</span>
          </div>
        </div>
      )}

      {/* Left Side - Dark Blue with Background Image */}
      <div className="hidden lg:flex lg:w-[42%] relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_3680826b-37cd-46b5-9360-3e7aa0a2e5d2/artifacts/ffzytdsl_Losspay%20Secure%20Login%20pic.webp)'
          }}
        />
        {/* Darkening overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/[0.65]" />
        
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          {/* Logo - Color version */}
          <div>
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-9"
            />
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex flex-col justify-center max-w-md -mt-10">
            <h1 className="text-[42px] font-bold text-white leading-[1.15] mb-5">
              Protect Your<br />
              <span className="text-[#60A5FA]">Vehicle's Value</span>
            </h1>
            <p className="text-[#94A3B8] text-[17px] leading-relaxed mb-10">
              LossPay helps protect the resale value of your vehicle after a qualifying repair event.
            </p>
            
            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#60A5FA] flex-shrink-0" />
                <span className="text-white text-[15px]">Fast VIN based claims verification</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#60A5FA] flex-shrink-0" />
                <span className="text-white text-[15px]">Transparent coverage designed for vehicle equity protection</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-[#60A5FA] flex-shrink-0" />
                <span className="text-white text-[15px]">Dedicated policyholder support</span>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-start gap-3 text-[#64748B] text-[13px]">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>Your information is protected using secure encryption and industry standard safeguards.</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col bg-[#F8FAFC]">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-5 py-4">
          <a href="https://losspay.com">
            <img 
              src="https://images.losspay.com/losspay/losspay-logo.png" 
              alt="LossPay" 
              className="h-8"
            />
          </a>
        </header>

        {/* Main Form Area */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-[400px]">
            
            {/* Login Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 p-8">
              
              {/* Step: Email/Password */}
              {step === 'email' && (
                <>
                  <div className="text-center mb-7">
                    <h1 className="text-[26px] font-bold text-[#0F172A] mb-2">
                      Welcome back
                    </h1>
                    <p className="text-[#64748B] text-[15px]">
                      Sign in to access your policyholder dashboard
                    </p>
                  </div>
                  
                  {error && (
                    <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                  
                  <form onSubmit={handleEmailSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-[#475569] font-semibold text-[11px] uppercase tracking-wider">
                        Email Address
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full h-[48px] pl-11 pr-4 rounded-xl border border-[#E2E8F0] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 transition-all text-[15px]"
                          disabled={loading}
                          data-testid="login-email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-[#475569] font-semibold text-[11px] uppercase tracking-wider">
                        Password
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#94A3B8]" />
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          className="w-full h-[48px] pl-11 pr-12 rounded-xl border border-[#E2E8F0] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 transition-all text-[15px]"
                          disabled={loading}
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="remember" 
                          checked={rememberMe}
                          onCheckedChange={setRememberMe}
                          className="w-4 h-4 border-[#CBD5E1] bg-white data-[state=checked]:bg-white data-[state=checked]:border-[#CBD5E1] data-[state=checked]:text-black"
                        />
                        <label htmlFor="remember" className="text-[14px] text-[#475569] cursor-pointer select-none">
                          Remember me
                        </label>
                      </div>
                      <a href="/portal/forgot-password" className="text-[14px] text-[#3B82F6] hover:text-[#2563EB] font-medium">
                        Forgot password?
                      </a>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full h-[48px] bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl font-semibold text-[15px] flex items-center justify-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="h-5 w-5" />
                        </>
                      )}
                    </button>
                  </form>
                  
                  <div className="mt-7 pt-6 border-t border-[#E2E8F0] text-center">
                    <p className="text-[#475569] text-[14px]">
                      First time? Activate your account{' '}
                      <a href="/policyholder/setup" className="text-[#3B82F6] hover:text-[#2563EB] font-semibold">
                        here
                      </a>
                    </p>
                  </div>
                </>
              )}

              {/* Step: OTP Verification */}
              {step === 'otp' && (
                <>
                  <div className="text-center mb-7">
                    <div className="w-14 h-14 bg-[#EFF6FF] rounded-xl flex items-center justify-center mx-auto mb-4">
                      <Mail className="h-7 w-7 text-[#3B82F6]" />
                    </div>
                    <h1 className="text-[24px] font-bold text-[#0F172A] mb-2">
                      Check your email
                    </h1>
                    <p className="text-[#64748B] text-[15px]">
                      We sent a verification code to<br />
                      <span className="font-medium text-[#0F172A]">{email}</span>
                    </p>
                  </div>
                  
                  {error && (
                    <div className="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                  
                  {/* OTP Input */}
                  <div className="flex justify-center gap-2.5 mb-6">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-11 h-12 text-center text-xl font-bold border-2 border-[#E2E8F0] rounded-lg focus:border-[#3B82F6] focus:ring-2 focus:ring-[#3B82F6]/20 outline-none transition-all bg-white"
                        disabled={loading}
                        data-testid={`otp-input-${index}`}
                      />
                    ))}
                  </div>
                  
                  <button
                    onClick={() => handleOtpSubmit()}
                    disabled={loading || otp.some(d => !d)}
                    className="w-full h-[48px] bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-xl font-semibold text-[15px] flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="verify-otp-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Verify Code'
                    )}
                  </button>
                  
                  <div className="mt-5 text-center">
                    <p className="text-[#64748B] text-[14px]">
                      Didn't receive the code?{' '}
                      <button 
                        onClick={handleResend}
                        disabled={loading}
                        className="text-[#3B82F6] hover:text-[#2563EB] font-medium"
                      >
                        Resend
                      </button>
                    </p>
                  </div>
                  
                  <button
                    onClick={() => { setStep('email'); setError(null); }}
                    className="mt-4 w-full text-center text-[#64748B] hover:text-[#475569] text-[14px]"
                  >
                    ← Back to login
                  </button>
                </>
              )}

              {/* Step: Success */}
              {step === 'success' && (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <CheckCircle className="h-9 w-9 text-green-600" />
                  </div>
                  <h1 className="text-[24px] font-bold text-[#0F172A] mb-2">
                    Welcome back!
                  </h1>
                  <p className="text-[#64748B] text-[15px] mb-5">
                    Redirecting to your dashboard...
                  </p>
                  <Loader2 className="h-6 w-6 animate-spin text-[#3B82F6] mx-auto" />
                </div>
              )}
              
            </div>
            
            {/* Security Note - Mobile Only */}
            <div className="lg:hidden mt-6 flex items-start gap-3 text-[#94A3B8] text-[13px] px-1">
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>Your information is protected using secure encryption and industry standard safeguards.</p>
            </div>
            
            {/* Legal Links */}
            <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2">
              <a 
                href="https://losspay.com/StateNotices"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-[#3B5BDB] transition-colors"
              >
                State Notices
              </a>
              <a 
                href="https://losspay.com/PrivacyPolicy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-[#3B5BDB] transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="https://losspay.com/TermsOfUse"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-slate-400 hover:text-[#3B5BDB] transition-colors"
              >
                Terms of Service
              </a>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
}
